import React, { useRef } from "react";
import _ from "lodash";
import { Braces } from "lucide-react";
import { Button, DropdownMenu, Input, Label } from "@/components/ui";
import { LiquidPlaceholderGroups } from "@/utils/liquidHelpers";

const insertAtSelection = (value: string, text: string, start: number, end: number) => ({
  value: value.slice(0, start) + text + value.slice(end),
  caret: start + text.length,
});

interface Props {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholders: LiquidPlaceholderGroups;
  placeholder?: string;
  maxLength?: number;
  error?: string | null;
}

const LiquidTextField: React.FC<Props> = ({
  id,
  label,
  value,
  onChange,
  placeholders,
  placeholder,
  maxLength,
  error,
}) => {
  const fieldRef = useRef<HTMLInputElement>(null);
  const selection = useRef({ start: value.length, end: value.length });
  const groups = Object.entries(placeholders).filter(([, entries]) => Object.keys(entries).length > 0);

  const rememberSelection = () => {
    const field = fieldRef.current;
    if (field)
      selection.current = { start: field.selectionStart ?? value.length, end: field.selectionEnd ?? value.length };
  };

  const insertPlaceholder = (path: string) => {
    const start = Math.min(selection.current.start, value.length);
    const end = Math.min(selection.current.end, value.length);
    const next = insertAtSelection(value, `{{ ${path} }}`, start, end);
    onChange(next.value);
    selection.current = { start: next.caret, end: next.caret };
    requestAnimationFrame(() => {
      fieldRef.current?.focus();
      fieldRef.current?.setSelectionRange(next.caret, next.caret);
    });
  };

  const fieldProps = {
    id,
    ref: fieldRef,
    value,
    placeholder,
    maxLength,
    "aria-invalid": !!error || undefined,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value),
    onSelect: rememberSelection,
    onBlur: rememberSelection,
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={id}>{label}</Label>
        {groups.length > 0 && (
          <DropdownMenu>
            <DropdownMenu.Trigger
              render={props => (
                <Button
                  {...props}
                  type="button"
                  variant="ghost"
                  size="xs"
                  className="text-slate-500 hover:text-slate-800"
                >
                  <Braces />
                  Insert placeholder
                </Button>
              )}
            />
            <DropdownMenu.Content align="end" className="max-h-80">
              {groups.map(([group, entries]) => (
                <DropdownMenu.Group key={group}>
                  <DropdownMenu.Label>{_.startCase(group)}</DropdownMenu.Label>
                  {Object.entries(entries).map(([displayName, path]) => (
                    <DropdownMenu.Item key={path} onClick={() => insertPlaceholder(path)}>
                      {displayName}
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.Group>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu>
        )}
      </div>
      <Input {...fieldProps} />
      {(error || maxLength) && (
        <div className="flex items-start justify-between gap-2 text-xs">
          {error && <p className="text-destructive">{error}</p>}
          {maxLength && (
            <span className="ml-auto shrink-0 text-slate-400">
              {value.length}/{maxLength}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export { LiquidTextField, insertAtSelection };
